import { Camera, PackagePlus, Plus } from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";

import { EmptyState } from "../components/EmptyState";
import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { isOfflineQueued } from "../lib/offline-link";
import { DEPENDENCY_PLACEHOLDER, type OutboxOperation } from "../lib/offline-outbox";
import { uploadThroughOutbox } from "../lib/offline-upload";
import { useOutboxOperations } from "../lib/use-offline-outbox";
import { matchesCollectionSearch } from "../lib/collection-search";
import { useSectionSearch } from "../lib/section-search";
import {
  materialRequestColumns,
  toMaterialRequestRecord,
  toQueuedMaterialRequestRecord,
  type QueuedMaterialRequestPayload,
} from "../lib/purchasing-collection-records";
import type { UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";
import { MATERIAL_UNIT_LABELS } from "../../shared/material-units";

const unitLabels = MATERIAL_UNIT_LABELS;

const statusLabels = {
  requested: "Richiesto",
  ordered: "Ordinato",
  partially_received: "Arrivato in parte",
  received: "Ricevuto",
  used: "Utilizzato",
  cancelled: "Annullato",
} as const;

type RequestStatus = keyof typeof statusLabels;

type UsageDraft = {
  quantity: string;
  notes: string;
  managerOverride: boolean;
  reason: string;
};

const emptyUsageDraft: UsageDraft = {
  quantity: "",
  notes: "",
  managerOverride: false,
  reason: "",
};

/**
 * La fotografia passa dal punto unico come tutto il resto. Quando la richiesta e ancora
 * in coda, l'indirizzo non esiste: al suo posto va il segnaposto, che la coda riempie da
 * sola con il numero che il server assegna alla richiesta.
 */
function inviaFotografia(photo: Blob, richiesta: { requestId: string } | { operationId: string }) {
  const inCoda = "operationId" in richiesta;
  return uploadThroughOutbox({
    url: `/api/uploads/material-requests/${inCoda ? DEPENDENCY_PLACEHOLDER : richiesta.requestId}`,
    field: "file",
    filename: "foto-materiale",
    blob: photo,
    label: "Acquisti: fotografia della richiesta materiale",
    dependsOn: inCoda ? { operationId: richiesta.operationId, field: "request.id" } : null,
  });
}

/** La richiesta come e stata scritta sul dispositivo: il corpo in coda e JSON, non un oggetto. */
function payloadInCoda(operation: OutboxOperation): QueuedMaterialRequestPayload | null {
  if (operation.kind !== "trpc" || !operation.body) return null;
  try {
    return JSON.parse(operation.body) as QueuedMaterialRequestPayload;
  } catch {
    return null;
  }
}

function localStartOfDay(value: string) {
  return value ? new Date(`${value}T00:00:00`) : undefined;
}

function timeValue(value: string | Date) {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

export function PurchaseRequestsPage({ session }: { session: UserSession }) {
  const utils = trpc.useUtils();
  const sites = trpc.sites.list.useQuery();
  const materials = trpc.materials.list.useQuery();
  const create = trpc.purchasing.requests.create.useMutation();
  const mapMaterial = trpc.purchasing.requests.mapMaterial.useMutation();
  const useMaterial = trpc.purchasing.requests.use.useMutation();
  const [viewMode, setViewMode] = useViewMode("richieste-materiali");
  const [open, setOpen] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState("");
  const [unit, setUnit] = useState<keyof typeof unitLabels>("piece");
  const [statusFilter, setStatusFilter] = useState<"all" | RequestStatus>("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [materialFilter, setMaterialFilter] = useState("all");
  const [requesterFilter, setRequesterFilter] = useState("all");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [search, setSearch] = useSectionSearch();
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [usageRequestId, setUsageRequestId] = useState<string | null>(null);
  const [usageDrafts, setUsageDrafts] = useState<Record<string, UsageDraft>>({});
  const usageOperationIds = useRef(new Map<string, string>());
  // Quello che il dispositivo tiene ancora per se. A farlo partire ci pensa l'app intera,
  // qui si legge soltanto, cosi la richiesta appena scritta si vede subito nel suo elenco.
  const outbox = useOutboxOperations(session.company.id, session.user.id);
  const queuedRequests = useMemo(
    () => outbox.operations
      .filter((operation) =>
        (operation.kind === "trpc" && operation.target === "purchasing.requests.create")
        || (operation.kind === "upload" && operation.target.includes("/api/uploads/material-requests/")))
      .map((operation) => ({ operation, payload: payloadInCoda(operation) })),
    [outbox.operations],
  );
  const requestFilters = useMemo(() => ({
    status: statusFilter === "all" ? undefined : statusFilter,
    siteId: siteFilter === "all" ? undefined : siteFilter,
    materialId: materialFilter === "all" ? undefined : materialFilter,
    requestedByUserId: requesterFilter === "all" ? undefined : requesterFilter,
    from: localStartOfDay(fromFilter),
    to: localStartOfDay(toFilter),
    search: search.trim() || undefined,
  }), [fromFilter, materialFilter, requesterFilter, search, siteFilter, statusFilter, toFilter]);
  const requests = trpc.purchasing.requests.list.useQuery(requestFilters);
  const canManage = session.membership.role !== "collaborator";
  const selectedMaterial = materials.data?.find((material) => material.id === selectedMaterialId);
  const requesterOptions = useMemo(() => {
    const options = new Map([[session.user.id, session.user.name]]);
    for (const request of requests.data ?? []) {
      options.set(
        request.requestedByUserId,
        request.requestedByName
          || (request.requestedByUserId === session.user.id ? session.user.name : "Richiedente"),
      );
    }
    if (requesterFilter !== "all" && !options.has(requesterFilter)) {
      options.set(requesterFilter, "Richiedente selezionato");
    }
    return Array.from(options, ([id, name]) => ({ id, name }))
      .sort((first, second) => first.name.localeCompare(second.name, "it"));
  }, [requesterFilter, requests.data, session.user.id, session.user.name]);
  const filtered = useMemo(
    () => {
      const from = localStartOfDay(fromFilter)?.getTime();
      const to = localStartOfDay(toFilter)?.getTime();
      return (requests.data ?? []).filter((request) =>
        (statusFilter === "all" || request.status === statusFilter)
        && (siteFilter === "all" || request.siteId === siteFilter)
        && (materialFilter === "all" || request.materialId === materialFilter)
        && (requesterFilter === "all" || request.requestedByUserId === requesterFilter)
        && (from === undefined || timeValue(request.createdAt) >= from)
        && (to === undefined || timeValue(request.createdAt) < to)
        && matchesCollectionSearch(search, [request.materialName, request.freeLabel, request.siteName, request.notes, request.requestedByName]),
      );
    },
    [fromFilter, materialFilter, requesterFilter, requests.data, search, siteFilter, statusFilter, toFilter],
  );
  const filteredQueued = useMemo(() => {
    const from = localStartOfDay(fromFilter)?.getTime();
    const to = localStartOfDay(toFilter)?.getTime();
    return queuedRequests.filter(({ operation, payload }) => {
      const createdAt = timeValue(operation.createdAt);
      const matchesCommon = (requesterFilter === "all" || operation.userId === requesterFilter)
        && (from === undefined || createdAt >= from)
        && (to === undefined || createdAt < to);
      if (!payload) {
        return matchesCommon
          && statusFilter === "all"
          && siteFilter === "all"
          && materialFilter === "all"
          && !search.trim();
      }
      const material = materials.data?.find((item) => item.id === payload.materialId);
      const site = sites.data?.find((item) => item.id === payload.siteId);
      return matchesCommon
        && (statusFilter === "all" || statusFilter === "requested")
        && (siteFilter === "all" || payload.siteId === siteFilter)
        && (materialFilter === "all" || payload.materialId === materialFilter)
        && matchesCollectionSearch(search, [material?.name, payload.freeLabel, site?.name, payload.notes, session.user.name]);
    });
  }, [fromFilter, materialFilter, materials.data, queuedRequests, requesterFilter, search, session.user.name, siteFilter, sites.data, statusFilter, toFilter]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setDuplicateWarning(null);
    setNotice(null);
    try {
      const clientRequestId = crypto.randomUUID();
      const payload = {
        siteId: String(data.get("siteId")),
        interventionId: null,
        materialId: selectedMaterialId || null,
        freeLabel: selectedMaterialId ? null : String(data.get("freeLabel") || "") || null,
        requestedQuantity: Number(data.get("requestedQuantity")),
        unit: String(data.get("unit")) as keyof typeof unitLabels,
        notes: String(data.get("notes") || "") || null,
      };
      const photoInput = form.elements.namedItem("photo") as HTMLInputElement | null;
      const photo = photoInput?.files?.[0] && photoInput.files[0].size > 0 ? photoInput.files[0] : null;

      // Nessun ramo per la rete assente: la mutazione parte comunque e il punto unico
      // decide. Senza rete torna la ricevuta della coda al posto del dato del server.
      const result = await create.mutateAsync({ ...payload, clientRequestId });
      setOpen(false);
      setSelectedMaterialId("");
      setUnit("piece");
      form.reset();

      if (isOfflineQueued(result)) {
        if (photo) await inviaFotografia(photo, { operationId: result.operationId });
        setNotice("Richiesta salvata sul dispositivo. Verrà inviata da sola appena torna la rete.");
        return;
      }

      await utils.purchasing.requests.list.invalidate();
      setDuplicateWarning(
        result.duplicateWarning ? "Esiste già una richiesta simile ancora aperta." : null,
      );
      if (photo) {
        const esito = await inviaFotografia(photo, { requestId: result.request.id });
        if (!esito.inviato) {
          setNotice("Richiesta salvata. La fotografia parte da sola appena torna la rete.");
          return;
        }
      }
      setNotice("Richiesta salvata.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Richiesta non salvata.");
    }
  }

  async function connectMaterial(requestId: string, materialId: string) {
    if (!materialId) return;
    await mapMaterial.mutateAsync({ requestId, materialId });
    await utils.purchasing.requests.list.invalidate();
  }

  async function submitUsage(
    event: FormEvent<HTMLFormElement>,
    requestId: string,
    unit: keyof typeof unitLabels,
  ) {
    event.preventDefault();
    const draft = usageDrafts[requestId] ?? emptyUsageDraft;
    const clientOperationId = usageOperationIds.current.get(requestId) ?? crypto.randomUUID();
    usageOperationIds.current.set(requestId, clientOperationId);
    try {
      await useMaterial.mutateAsync({
        clientOperationId,
        requestId,
        quantity: Number(draft.quantity),
        unit,
        notes: draft.notes || null,
        managerOverride: canManage && draft.managerOverride,
        reason: draft.reason || null,
      });
      await utils.purchasing.requests.list.invalidate();
      usageOperationIds.current.delete(requestId);
      setUsageDrafts((current) => {
        const next = { ...current };
        delete next[requestId];
        return next;
      });
      setUsageRequestId(null);
      setNotice("Utilizzo registrato.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Utilizzo non registrato.");
    }
  }

  function updateUsageDraft(requestId: string, update: Partial<UsageDraft>) {
    setUsageDrafts((current) => ({
      ...current,
      [requestId]: { ...(current[requestId] ?? emptyUsageDraft), ...update },
    }));
  }

  const queuedForDisplay = requests.error ? queuedRequests : filteredQueued;
  const queuedRecords = queuedForDisplay.map(({ operation, payload }, index) => {
    const actions = (
      <div className="button-group">
        <button className="button button--outline" type="button" onClick={() => void outbox.riprova(operation.id)}>Riprova</button>
        <button className="button button--ghost" type="button" onClick={() => void outbox.elimina(operation.id)}>Elimina</button>
      </div>
    );
    return toQueuedMaterialRequestRecord(operation, payload, index, {
      materialName: payload ? materials.data?.find((item) => item.id === payload.materialId)?.name : undefined,
      siteName: payload ? sites.data?.find((item) => item.id === payload.siteId)?.name : undefined,
      requesterName: session.user.name,
      actions,
    });
  });
  const requestRecords = filtered.map((request, index) => toMaterialRequestRecord(request, queuedRecords.length + index, (
    <>
      {canManage && !request.materialId && (
        <label className="select-control purchase-request-card__mapping">
          <span>Collega al catalogo</span>
          <select defaultValue="" onChange={(event) => void connectMaterial(request.id, event.target.value)}>
            <option value="">Scegli materiale</option>
            {materials.data?.filter((material) => material.baseUnit === request.unit).map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}
          </select>
        </label>
      )}
      {request.availableQuantity > 0 && (
        <button className="text-button purchase-request-card__usage" type="button" onClick={() => setUsageRequestId(usageRequestId === request.id ? null : request.id)}>Registra utilizzo</button>
      )}
      {usageRequestId === request.id && (
        <form className="usage-form" onSubmit={(event) => void submitUsage(event, request.id, request.unit)}>
          <label><span>Quantità utilizzata</span><input name="quantity" type="number" min="0.0001" step="any" required value={(usageDrafts[request.id] ?? emptyUsageDraft).quantity} onChange={(event) => updateUsageDraft(request.id, { quantity: event.target.value })} /></label>
          <label><span>Note utilizzo</span><input name="notes" value={(usageDrafts[request.id] ?? emptyUsageDraft).notes} onChange={(event) => updateUsageDraft(request.id, { notes: event.target.value })} /></label>
          {canManage && <><label className="check-control"><input name="managerOverride" type="checkbox" checked={(usageDrafts[request.id] ?? emptyUsageDraft).managerOverride} onChange={(event) => updateUsageDraft(request.id, { managerOverride: event.target.checked })} /> Correzione oltre la quantità disponibile</label><label><span>Motivazione correzione</span><input name="reason" value={(usageDrafts[request.id] ?? emptyUsageDraft).reason} onChange={(event) => updateUsageDraft(request.id, { reason: event.target.value })} /></label></>}
          {useMaterial.error && <p className="form-error" role="alert">{useMaterial.error.message}</p>}
          <button className="button button--primary" disabled={useMaterial.isPending}>Salva utilizzo</button>
        </form>
      )}
    </>
  )));
  const visibleRecords = [...queuedRecords, ...(requests.error ? [] : requestRecords)];
  const hasActiveFilters = Boolean(
    search.trim()
    || statusFilter !== "all"
    || siteFilter !== "all"
    || materialFilter !== "all"
    || requesterFilter !== "all"
    || fromFilter
    || toFilter,
  );

  return (
    <section className="purchase-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Materiali sul campo</p>
          <h1>Richieste materiali</h1>
          <p>Segnala ciò che serve, per quale struttura e in quale quantità.</p>
        </div>
        <div className="page-heading__actions">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <button className="button button--primary" type="button" onClick={() => setOpen(!open)}>
            <Plus size={18} /> Nuova richiesta
          </button>
        </div>
      </header>

      {open && (
        <form className="editor-card" onSubmit={submit}>
          <div className="editor-card__heading">
            <h2>Nuova richiesta materiale</h2>
            <p>Puoi scegliere dal catalogo oppure scrivere liberamente ciò che manca.</p>
          </div>
          <div className="form-grid">
            <label>
              <span>Struttura</span>
              <select name="siteId" required defaultValue="">
                <option value="" disabled>Seleziona struttura</option>
                {sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
              </select>
            </label>
            <label>
              <span>Dal catalogo, facoltativo</span>
              <select
                name="materialId"
                value={selectedMaterialId}
                onChange={(event) => {
                  const materialId = event.target.value;
                  setSelectedMaterialId(materialId);
                  const material = materials.data?.find((item) => item.id === materialId);
                  if (material) setUnit(material.baseUnit);
                }}
              >
                <option value="">Descrizione libera</option>
                {materials.data?.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}
              </select>
            </label>
            <label className="form-grid__wide">
              <span>Materiale o descrizione</span>
              <input
                name="freeLabel"
                disabled={Boolean(selectedMaterialId)}
                required={!selectedMaterialId}
                maxLength={160}
                placeholder="Cloro granulare, pittura bianca, raccordo..."
              />
            </label>
            <label>
              <span>Quantità</span>
              <input name="requestedQuantity" type="number" min="0.0001" step="any" required />
            </label>
            <label>
              <span>Unità</span>
              <select
                name="unit"
                value={unit}
                disabled={Boolean(selectedMaterial)}
                onChange={(event) => setUnit(event.target.value as keyof typeof unitLabels)}
              >
                {Object.entries(unitLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="form-grid__wide">
              <span>Note</span>
              <textarea name="notes" rows={3} placeholder="Urgenza, marca preferita o altre indicazioni" />
            </label>
            <label className="upload-button form-grid__wide">
              <Camera size={18} /> <span>Fotografia facoltativa</span>
              <input name="photo" type="file" accept="image/jpeg,image/png,image/heic" />
            </label>
          </div>
          {create.error && <p className="form-error">{create.error.message}</p>}
          <div className="form-actions">
            <button className="button button--ghost" type="button" onClick={() => setOpen(false)}>Annulla</button>
            <button className="button button--primary" disabled={create.isPending}>Salva richiesta</button>
          </div>
        </form>
      )}

      {duplicateWarning && <p className="notice">{duplicateWarning}</p>}
      {notice && <p className="notice" role="status">{notice}</p>}

      <div className="purchase-filters">
        <CollectionSearch label="Cerca richieste" value={search} onChange={setSearch} placeholder="Materiale, struttura o note" />
        <label><span>Stato</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">Tutti</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label><span>Filtra struttura</span><select value={siteFilter} onChange={(event) => setSiteFilter(event.target.value)}><option value="all">Tutte</option>{sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
        <label><span>Filtra materiale</span><select value={materialFilter} onChange={(event) => setMaterialFilter(event.target.value)}><option value="all">Tutti</option>{materials.data?.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}</select></label>
        <label><span>Richiedente</span><select value={requesterFilter} onChange={(event) => setRequesterFilter(event.target.value)}><option value="all">Tutti</option>{requesterOptions.map((requester) => <option key={requester.id} value={requester.id}>{requester.name}</option>)}</select></label>
        <label><span>Dal giorno</span><input type="date" value={fromFilter} onChange={(event) => setFromFilter(event.target.value)} /></label>
        <label><span>Prima del giorno</span><input type="date" value={toFilter} onChange={(event) => setToFilter(event.target.value)} /></label>
      </div>

      {requests.error && <div className="query-error" role="alert"><strong>Richieste non disponibili</strong><span>{requests.error.message}</span><button className="button button--outline" type="button" onClick={() => void requests.refetch()}>Riprova</button></div>}
      {visibleRecords.length > 0 && <CollectionView
        mode={viewMode}
        ariaLabel="Richieste materiali"
        titleLabel="Richiesta"
        columns={materialRequestColumns}
        records={visibleRecords}
        print={{
          section: "Richieste materiali",
          details: [
            statusFilter === "all" ? null : `Stato: ${statusLabels[statusFilter]}`,
            siteFilter === "all" ? null : `Struttura: ${sites.data?.find((struttura) => struttura.id === siteFilter)?.name ?? ""}`,
            fromFilter || toFilter ? `Periodo: ${fromFilter || "inizio"} - ${toFilter || "oggi"}` : null,
            search ? `Ricerca: ${search}` : null,
          ],
        }}
      />}
      {!requests.error && !requests.isLoading && visibleRecords.length === 0 && (hasActiveFilters
        ? <EmptyState title="Nessun risultato">Modifica la ricerca o i filtri per vedere altre richieste.</EmptyState>
        : <EmptyState title="Nessuna richiesta">Le richieste di materiale compariranno qui.</EmptyState>)}
      {requests.isLoading && <p className="loading-copy"><PackagePlus size={18} /> Caricamento richieste...</p>}
    </section>
  );
}
